import { DataNode, ScrollableTreeRef } from 'components/WorkspaceEditor/types'
import { MutableRefObject, useCallback } from 'react'
import {
    areParentNodesExpanded,
    expandTreePath,
    isValidTreeRef,
    SCROLL_CONFIG,
    scrollToContentElement,
} from '../utils/TreeNodesUtils'
import { useMemoizedTreeSearch } from './useMemoizedTreeSearch'

// interface ScrollableTreeRef {
//     scrollTo(options: { key: string; align: 'auto' }): Promise<void>
// }

/**
 * Hook to scroll to a node in an Ant Design Tree with auto-expansion support.
 * @param treeRef - ref to the Ant Tree component (should be passed to <Tree ref={treeRef} />)
 * @param getNodeInfoById - function to get the tree node info including path by content id
 * @param expandedKeys - current expanded keys from tree state
 * @param setExpandedKeys - function to update expanded keys
 */
export const useScrollToContent = (
    treeRef: MutableRefObject<ScrollableTreeRef>,
    expandedKeys: React.Key[],
    setExpandedKeys: (keys: React.Key[]) => void,
    treeData: DataNode[]
) => {
    const isDevelopment = process.env.NODE_ENV === 'development'

    const getTreeNodeInfoById = useMemoizedTreeSearch(treeData)
    return useCallback(
        async (id: string) => {
            const nodeInfo = getTreeNodeInfoById(id)
            if (!nodeInfo) {
                console.warn(
                    `useScrollToContent: No node info found for id ${id}`
                )
                return
            }

            // 1. Check if parent nodes need to be expanded
            const needsExpansion = !areParentNodesExpanded(
                nodeInfo.pathToNode,
                expandedKeys
            )

            if (needsExpansion) {
                try {
                    await expandTreePath(
                        expandedKeys,
                        nodeInfo.pathToNode,
                        setExpandedKeys
                    )
                } catch (error) {
                    console.error('Error expanding tree path:', error)
                }
            }

            // 2. Use Ant Tree's scrollTo to bring the node into view
            if (isValidTreeRef(treeRef)) {
                try {
                    await treeRef.current.scrollTo({
                        key: nodeInfo.nodeKey,
                        align: 'auto',
                    })
                } catch (error) {
                    console.error(
                        'Error scrolling tree to key:',
                        nodeInfo.nodeKey,
                        error
                    )
                }
            } else {
                isDevelopment &&
                    console.warn(
                        'Tree ref not available or scrollTo method not found',
                        {
                            current: treeRef.current,
                            hasScrollTo: isValidTreeRef(treeRef),
                        }
                    )
            }

            // 3. Use requestAnimationFrame to ensure tree operations complete first
            requestAnimationFrame(() => {
                // Add a delay to allow tree scroll and expansion to complete
                const delay = needsExpansion
                    ? SCROLL_CONFIG.DELAYS.SCROLL_WITH_EXPANSION
                    : SCROLL_CONFIG.DELAYS.SCROLL_WITHOUT_EXPANSION
                setTimeout(async () => {
                    const success = await scrollToContentElement(id)

                    if (!success) {
                        if (isDevelopment) {
                            console.warn(
                                `Failed to scroll to content element for id: ${id}`
                            )
                        }
                    }
                }, delay)
            })
        },
        [getTreeNodeInfoById, expandedKeys, setExpandedKeys, treeRef, treeData]
    )
}
